import { Typography, Badge, formatPrice } from '@lucid/lego';
import type { ImporterOrder } from '../../../../api/services/lupp/order/importer/dto.importer';
import { formatDate } from '../../../../utils/tools';
import { getImporterOrderStatusBadgeColor } from '../../constants';
import styles from './Orders.module.scss';

const formatDisplayPrice = (amount: number, locale: string): string =>
  formatPrice(amount, { locale });

export const getImporterOrderStatusLabel = (
  status: string,
  labels: Record<string, string>
): string => {
  const normalizedStatus = status.toLowerCase().replace(/_/g, ' ');
  const map: Record<string, string> = {
    initiated: labels['status.initiated'] ?? 'Initiated',
    'partially fulfilled': labels['status.partiallyFilled'] ?? 'Partially Fulfilled',
    'ready for dispatch': labels['status.readyForDispatch'] ?? 'Ready for Pickup',
    fulfilled: labels['status.fulfilled'] ?? 'Fulfilled',
    canceled: labels['status.canceled'] ?? 'Canceled',
    cancelled: labels['status.canceled'] ?? 'Canceled',
  };
  return map[normalizedStatus] ?? normalizedStatus.replace(/\b\w/g, (c) => c.toUpperCase());
};

type CreateColumnsParams = {
  theme: 'light' | 'dark';
  labels: Record<string, string>;
  locale: string;
  onImporterOrderClick?: (record: ImporterOrder) => void;
};

/**
 * Column definitions for the orders table
 */
export const createOrderColumns = ({
  theme,
  labels,
  locale,
  onImporterOrderClick,
}: CreateColumnsParams) => [
  {
    key: 'statusName',
    title: labels?.['table.status'] ?? 'Order Status',
    sortable: true,
    render: (_: unknown, record: ImporterOrder) => {
      const status = record.statusName;
      if (!status) return <span>-</span>;
      return (
        <Badge
          label={getImporterOrderStatusLabel(status, labels)}
          type="primary"
          color={getImporterOrderStatusBadgeColor(status)}
          theme={theme === 'dark' ? 'light' : 'dark'}
          minWidth="60px"
        />
      );
    },
  },
  {
    key: 'importerOrderNumber',
    title: labels?.['table.ioNumber'] ?? 'Parent Order #',
    sortable: true,
    render: (_: unknown, record: ImporterOrder) => (
      <Typography
        variant="body"
        size="small"
        color={onImporterOrderClick ? 'link' : 'black'}
        theme={theme}
        onClick={onImporterOrderClick ? () => onImporterOrderClick(record) : undefined}
        className={onImporterOrderClick ? styles.clickable : undefined}
      >
        {record.importerOrderNumber}
      </Typography>
    ),
  },
  {
    key: 'totalVins',
    title: labels?.['table.itemCount'] ?? 'Item Count',
    sortable: true,
    render: (_: unknown, record: ImporterOrder) => (
      <Typography variant="body" size="small" color="black">
        {record.totalVins ?? '-'}
      </Typography>
    ),
  },
  {
    key: 'totalAmount',
    title: labels?.['table.amount'] ?? 'Order Total',
    sortable: true,
    render: (_: unknown, record: ImporterOrder) => (
      <Typography variant="body" size="small" color="black">
        {formatDisplayPrice(record.totalAmount ?? 0, locale)}
      </Typography>
    ),
  },
  {
    key: 'orderDate',
    title: labels?.['table.orderDate'] ?? 'Order Date',
    sortable: true,
    render: (_: unknown, record: ImporterOrder) => (
      <Typography variant="body" size="small" color="black">
        {formatDate(record.orderDate)}
      </Typography>
    ),
  },
  {
    key: 'updatedAt',
    title: labels?.['table.lastUpdated'] ?? 'Last Updated',
    sortable: true,
    render: (_: unknown, record: ImporterOrder) => (
      <Typography variant="body" size="small" color="black">
        {formatDate(record.updatedAt)}
      </Typography>
    ),
  },
];
